import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { gatewayRequestAxios } from '../../api/interceptor'
import { GATEWAY_API_LOGIN_HISTORY } from '../../util/config'

const historyState = { total: 0, history: [] }

const initialState = {
  loginHistoryObj: { ...historyState },
  status: 'idle',
  error: null,
  currentRequestId: undefined,
  filters: {
    startDate: null,
    endDate: null,
  },
  pagination: {
    page: 0,
    rowsPerPage: 25
  },
  lastRequest: null,
}

const RANGE_START_PARAM = 'startTime'
const RANGE_END_PARAM = 'endTime'
const LIMIT_PARAM = 'limit'

export const fetchLoginHistory = createAsyncThunk(
  'loginHistory/fetch',
  async (payload, { rejectWithValue, signal }) => {
    try {
      const {
        pageSize = 25,
        pageOffset = 0,
        startDate,
        endDate,
      } = payload || {}

      const pageStart = pageOffset * pageSize
      const pageEnd = pageStart + pageSize

      const limit = 1000

      const params = {
        [RANGE_START_PARAM]: startDate,
        [RANGE_END_PARAM]: endDate,
        [LIMIT_PARAM]: limit,
        pageSize,
        pageOffset,
      }

      const response = await gatewayRequestAxios.get(GATEWAY_API_LOGIN_HISTORY, {
        params,
        signal,
      })

      const data = response.data || {}
      const all = Array.isArray(data.history) ? data.history : []

      const windowItems = all.slice(pageStart, pageEnd)

      const backendTotal = Number.isFinite(data.total) ? Number(data.total) : undefined

      let hasNext
      if (typeof backendTotal === 'number') {
        hasNext = backendTotal > pageEnd
      } else {
        hasNext = all.length > pageEnd
      }

      let totalForUi
      if (typeof backendTotal === 'number') {
        totalForUi = backendTotal
      } else if (hasNext) {
        totalForUi = pageEnd + 1
      } else {
        totalForUi = pageStart + windowItems.length
      }

      return {
        ...data,
        total: totalForUi,
        history: windowItems,
      }
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message
        || err?.message
        || 'Failed to fetch login history'
      )
    }
  },
  {
    condition: (_, { getState }) => {
      const { loginHistory } = getState()
      return loginHistory.status !== 'loading'
    },
  },
)

export const loginHistorySlice = createSlice({
  name: 'login-history',
  initialState,
  reducers: {
    setStartDate(state, action) {
      state.filters.startDate = action.payload
    },
    setEndDate(state, action) {
      state.filters.endDate = action.payload
    },
    setPage(state, action) {
      state.pagination.page = action.payload
    },
    setRowsPerPage(state, action) {
      state.pagination.rowsPerPage = action.payload
    },
    setLastRequest(state, action) {
      state.lastRequest = action.payload
    },
    clearHistoryFilters(state) {
      state.lastRequest = null

      state.filters.startDate = null
      state.filters.endDate = null

      state.pagination.page = 0
      state.pagination.rowsPerPage = 25
    },
    setPagination(state, action) {
      state.pagination = {
        ...state.pagination,
        ...action.payload,
      }
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchLoginHistory.pending, (state, action) => {
        state.status = 'loading'
        state.error = undefined
        state.currentRequestId = action.meta.requestId
      })
      .addCase(fetchLoginHistory.fulfilled, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) return
        state.status = 'succeeded'
        state.error = undefined
        state.loginHistoryObj = action.payload ?? { ...historyState }
        state.currentRequestId = undefined
      })
      .addCase(fetchLoginHistory.rejected, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) return
        state.status = 'failed'
        state.loginHistoryObj = { ...historyState }
        state.error = action.payload || action.error?.message
        state.currentRequestId = undefined
      })
  },
})

export const {
  setStartDate, setEndDate, setPage, setRowsPerPage, setLastRequest, clearHistoryFilters
} = loginHistorySlice.actions

export default loginHistorySlice.reducer

export const selectLoginHistoryObject = (state) => state.loginHistory.loginHistoryObj
export const getLoginHistoryStatus = (state) => state.loginHistory.status
export const getLoginHistoryError = (state) => state.loginHistory.error
export const getLoginHistoryFilter = (state) => state.loginHistory.filters
export const getLoginHistoryPagination = (state) => state.loginHistory.pagination
